import type { Composition, DiagnosticReport, DocumentReference, Reference } from '@medplum/fhirtypes';

/**
 * `Procedure.report[]` is `Reference<DiagnosticReport | DocumentReference>` and both kinds occur in
 * practice: a results-bearing report is a `DiagnosticReport`, while imported procedures
 * overwhelmingly link `DocumentReference`s (scanned operative notes, anaesthesia evaluations). They
 * render differently and are read by different endpoints, so a consumer has to split the list
 * before fetching — reading every entry as a `DiagnosticReport` 404s every document.
 *
 * Classification is by the reference string, which carries both the resource type and the id. The
 * optional `type` field is deliberately not used as a fallback: the references it would apply to
 * (`urn:uuid:`, absolute URLs) yield no id to fetch by anyway, so an entry whose reference is not a
 * plain `<Type>/<id>` is dropped rather than half-resolved.
 *
 * R4 also permits `Composition` here. We have no renderer for one, so those entries fall out with
 * everything else unrecognised rather than being mis-fetched as one of the two kinds we do handle.
 */
export function partitionProcedureReports(
  reports: Reference<Composition | DiagnosticReport | DocumentReference>[] | undefined,
): { diagnosticReportIds: string[]; documentReferenceIds: string[] } {
  const diagnosticReportIds: string[] = [];
  const documentReferenceIds: string[] = [];

  for (const report of reports ?? []) {
    const relative = /^(DiagnosticReport|DocumentReference)\/(.+)$/.exec(report.reference ?? '');
    if (!relative) continue;

    const [, resourceType, id] = relative;
    (resourceType === 'DiagnosticReport' ? diagnosticReportIds : documentReferenceIds).push(id!);
  }

  return { diagnosticReportIds, documentReferenceIds };
}
